import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/blocks/evo-sidebar";
import { Settings, ChevronRight, Shield, FileText, Bell, TrendingUp, Activity /*, FlaskConical hidden for phase 1 release (Feature Labs) */ } from "lucide-react";
import { ProjectsLogo } from "@/components/ProjectsLogo";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { useFavorites } from "./hooks/useFavorites";
import { DatabaseSwitcher } from "./components/DatabaseSwitcher";
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import type { LeftMenuProps, MenuItem } from "./types";
import { menuItems, databases, menuConfig } from "./config/menuItems";
import { useAuth } from "@/providers/AuthProvider";
import { useDataMode } from "@/contexts/DataModeContext";
import { CompanySelector } from "@/components/company-selector";
import { Separator } from "@/components/ui/separator";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { PlayCircle } from "lucide-react";
import { usePermissions } from "@/contexts/PermissionsContext";
import type { SettingsPermissions } from "@/types/roles.types";

/**
 * Left Menu Component (Modular Version)
 * 
 * Features:
 * - Collapsible sidebar (icon-only mode)
 * - Favorites system (disabled - only 9 pages, threshold is 10)
 * - Database/Environment switcher (disabled - company selector in header)
 * - Context menu for favoriting items
 * - Mobile responsive
 * - Keyboard shortcuts (Cmd/Ctrl + B)
 */
export function LeftMenuModular({ activePage, onPageChange }: LeftMenuProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { authenticatedUser, isProduction } = useAuth();
  const { isDemoMode } = useDataMode();
  const { openWizard, hasCompleted } = useOnboarding();
  // Settings section is only visible to users with "Administrator" ApplicationRole.
  // Always show in demo mode (so users can explore settings with sample data),
  // in non-production mode (dev/sandbox), or when the authenticated user is an Administrator.
  const isAdmin = isDemoMode || !isProduction || authenticatedUser?.applicationRole === "Administrator";
  const { canAccessSetting, settings: settingsPerms } = usePermissions();
  const hasAnySettingsAccess = isAdmin || (Object.values(settingsPerms) as boolean[]).some(Boolean);

  // Map sidebar sub-item ids to their corresponding settings permission key.
  // Items without a permission key (e.g. Feature Labs) are only shown when isAdmin.
  const settingsPermissionMap: Record<string, keyof SettingsPermissions | null> = {
    'settings-general': 'generalSettings',
    'settings-project': 'projectSettings',
    'settings-integrations': 'integrations',
    'settings-user-permissions': 'rolesAndPermissions',
    'settings-templates': 'templates',
    'settings-notifications': 'notifications',
    'settings-feature-labs': null,
  };

  const canShowSettingsItem = (page: string): boolean => {
    if (isAdmin) return true;
    const perm = settingsPermissionMap[page];
    return perm != null && canAccessSetting(perm);
  };

  const [selectedDatabase, setSelectedDatabase] = useState("production");
  const [activeItemId, setActiveItemId] = useState<string>("");
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    "resources-menu": false, // Resources menu collapsed by default
    "reports-menu": false, // Reports menu collapsed by default
    "settings": false, // Settings menu collapsed by default
  });

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  const favoriteItems = menuItems.filter((item) => isFavorite(item.id));

  // Sync activeItemId with activePage prop
  useEffect(() => {
    // Map page names to their menu item IDs
    const pageToItemId: Record<string, string> = {
      'dashboard': 'main-dashboard',
      'projects': 'main-projects',
      'project-templates': 'main-project-templates',
      'portfolio': 'main-portfolio',
      'directory': 'sub-directory',
      'roles-rates': 'sub-roles-rates',
      'utilisation': 'sub-utilisation',
      'skills': 'sub-skills',
      'project-transactions': 'sub-project-transactions',
      'wip-reporting': 'sub-wip-reporting',
      'wip': 'sub-wip-reporting',
      'project-status': 'sub-project-status',
      'budget-variance': 'sub-budget-variance',
      'project-pl': 'sub-project-pl',
      'project-forecast': 'sub-project-forecast',
      'audit-log': 'sub-audit-log',
      'profitability': 'sub-profitability',

      'gantt': 'main-gantt',
      'settings-general': 'sub-settings-general',
      'settings-project': 'sub-settings-project',
      'settings-user-preferences': 'sub-settings-user-preferences',
      'settings-integrations': 'sub-settings-integrations',
      'settings-feature-labs': 'sub-settings-feature-labs',
      'settings-user-permissions': 'sub-settings-user-permissions',
      'settings-templates': 'sub-settings-templates',
      'settings-notifications': 'sub-settings-notifications',
    };
    
    // Direct match, or fall back to first path segment for sub-routes (e.g. projects/123 → projects)
    let itemId = pageToItemId[activePage];
    if (!itemId) {
      const firstSegment = activePage.split('/')[0];
      itemId = pageToItemId[firstSegment];
    }

    if (itemId) {
      setActiveItemId(itemId);
      
      // Auto-open parent menus for sub-items, close unrelated menus for main items
      if (itemId.startsWith('sub-settings-')) {
        setOpenMenus(prev => ({ ...prev, 'settings': true }));
      } else if (itemId.startsWith('sub-') && activePage.includes('directory')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-') && activePage.includes('roles-rates')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-utilisation')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-skills')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-project-transactions') || itemId.startsWith('sub-wip-reporting') || itemId.startsWith('sub-audit-log') || itemId.startsWith('sub-project-status') || itemId.startsWith('sub-budget-variance') || itemId.startsWith('sub-project-pl') || itemId.startsWith('sub-project-forecast') || itemId.startsWith('sub-profitability')) {
        setOpenMenus(prev => ({ ...prev, 'reports-menu': true }));
      } else if (!itemId.startsWith('sub-')) {
        // Main menu item — collapse all groups (mirrors handleItemClick behaviour)
        setOpenMenus(prev => ({
          ...prev,
          'resources-menu': false,
          'reports-menu': false,
          'settings': false,
        }));
      }
    }
  }, [activePage]);

  const handleItemClick = (page: string, itemId: string) => {
    setActiveItemId(itemId);
    onPageChange(page);
    
    // Close Resources, Reports, and Settings menus when clicking on any main menu item (not sub-items)
    if (!itemId.startsWith('sub-')) {
      setOpenMenus(prev => ({
        ...prev,
        "resources-menu": false,
        "reports-menu": false,
        "settings": false
      }));
    }
  };

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const renderMenuItem = (item: MenuItem, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="relative h-full">
      {/* Header */}
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <SidebarTrigger />
          <ProjectsLogo className="h-6 w-6 flex-shrink-0 group-data-[state=collapsed]:hidden" />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[195px] group-data-[state=collapsed]:hidden text-neutral-700">
            Projects
          </h3>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show"></div>

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center">
        {/* Favorites Section - Only shows if enabled (currently disabled: 9 pages < 10 threshold) */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
              
              {/* Dashboard */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "dashboard")!, "main-dashboard")}
              </SidebarMenuItem>

              {/* Projects */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "projects")!, "main-projects")}
              </SidebarMenuItem>

              {/* Project Templates */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "project-templates")!, "main-project-templates")}
              </SidebarMenuItem>

              {/* Portfolio — hidden for Release 1 */}
              {!menuItems.find((item) => item.id === "portfolio")?.hidden && (
                <SidebarMenuItem>
                  {renderMenuItem(menuItems.find((item) => item.id === "portfolio")!, "main-portfolio")}
                </SidebarMenuItem>
              )}

              {/* Resources Menu (Collapsible with sub-items / Flyout when collapsed) */}
              {(() => {
                const resourcesMenu = menuItems.find((item) => item.id === "resources-menu")!;
                const ResourcesIcon = resourcesMenu.icon;
                const DirectoryIcon = resourcesMenu.children![0].icon;
                const RolesRatesIcon = resourcesMenu.children![1].icon;
                const UtilisationIcon = resourcesMenu.children![2].icon;
                const SkillsIcon = resourcesMenu.children![3].icon;

                if (isCollapsed) {
                  return (
                    <SidebarMenuItem>
                      <DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <SidebarMenuButton hasSubmenu>
                                <ResourcesIcon />
                              </SidebarMenuButton>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <span className="flex items-center gap-1">
                              Resources
                              <ChevronRight className="size-3 opacity-60" />
                            </span>
                          </TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent side="right" align="start" className="min-w-[180px]">
                          <DropdownMenuLabel>Resources</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleItemClick("directory", "sub-directory")}
                            className={activeItemId === "sub-directory" ? "bg-accent font-medium" : ""}
                          >
                            <DirectoryIcon className="size-4" />
                            Directory
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("roles-rates", "sub-roles-rates")}
                            className={activeItemId === "sub-roles-rates" ? "bg-accent font-medium" : ""}
                          >
                            <RolesRatesIcon className="size-4" />
                            Roles & Rates
                          </DropdownMenuItem>
                          {!resourcesMenu.children![2].hidden && (
                            <DropdownMenuItem
                              onClick={() => handleItemClick("utilisation", "sub-utilisation")}
                              className={activeItemId === "sub-utilisation" ? "bg-accent font-medium" : ""}
                            >
                              <UtilisationIcon className="size-4" />
                              Utilisation
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleItemClick("skills", "sub-skills")}
                            className={activeItemId === "sub-skills" ? "bg-accent font-medium" : ""}
                          >
                            <SkillsIcon className="size-4" />
                            Skills
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  );
                }
                
                return (
                  <SidebarMenuItem>
                    <Collapsible
                      open={openMenus["resources-menu"]}
                      onOpenChange={() => toggleMenu("resources-menu")}
                      className="group/collapsible"
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Resources" hasSubmenu>
                          <ResourcesIcon />
                          <span className="group-data-[collapsible=icon]:hidden">Resources</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Directory */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("directory", "sub-directory")}
                              data-active={activeItemId === "sub-directory"}
                              className={activeItemId === "sub-directory" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <DirectoryIcon />
                              <span>Directory</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Roles & Rates */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("roles-rates", "sub-roles-rates")}
                              data-active={activeItemId === "sub-roles-rates"}
                              className={activeItemId === "sub-roles-rates" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <RolesRatesIcon />
                              <span>Roles & Rates</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Utilisation — hidden for Release 1 */}
                          {!resourcesMenu.children![2].hidden && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                onClick={() => handleItemClick("utilisation", "sub-utilisation")}
                                data-active={activeItemId === "sub-utilisation"}
                                className={activeItemId === "sub-utilisation" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                              >
                                <UtilisationIcon />
                                <span>Utilisation</span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          {/* Skills */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("skills", "sub-skills")}
                              data-active={activeItemId === "sub-skills"}
                              className={activeItemId === "sub-skills" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <SkillsIcon />
                              <span>Skills</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })()}

              {/* Reports Menu (Collapsible with sub-items / Flyout when collapsed) */}
              {(() => {
                const reportsMenu = menuItems.find((item) => item.id === "reports-menu")!;
                const ReportsIcon = reportsMenu.icon;
                const ProjectTransactionsIcon = reportsMenu.children![0].icon;
                const WIPIcon = reportsMenu.children![1].icon;
                const BudgetVarianceIcon = reportsMenu.children![3].icon;
                const ProjectPLIcon = reportsMenu.children![4].icon;
                const ProfitabilityIcon = reportsMenu.children![6].icon;
                const AuditLogIcon = reportsMenu.children![7].icon;

                if (isCollapsed) {
                  return (
                    <SidebarMenuItem>
                      <DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <SidebarMenuButton hasSubmenu>
                                <ReportsIcon />
                              </SidebarMenuButton>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <span className="flex items-center gap-1">
                              Reports
                              <ChevronRight className="size-3 opacity-60" />
                            </span>
                          </TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent side="right" align="start" className="min-w-[180px]">
                          <DropdownMenuLabel>Reports</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleItemClick("project-transactions", "sub-project-transactions")}
                            className={activeItemId === "sub-project-transactions" ? "bg-accent font-medium" : ""}
                          >
                            <ProjectTransactionsIcon className="size-4" />
                            Project Transactions
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("wip-reporting", "sub-wip-reporting")}
                            className={activeItemId === "sub-wip-reporting" ? "bg-accent font-medium" : ""}
                          >
                            <WIPIcon className="size-4" />
                            WIP
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("project-status", "sub-project-status")}
                            className={activeItemId === "sub-project-status" ? "bg-accent font-medium" : ""}
                          >
                            <Activity className="size-4" />
                            Project Status
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("budget-variance", "sub-budget-variance")}
                            className={activeItemId === "sub-budget-variance" ? "bg-accent font-medium" : ""}
                          >
                            <BudgetVarianceIcon className="size-4" />
                            Budget Variance
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("project-pl", "sub-project-pl")}
                            className={activeItemId === "sub-project-pl" ? "bg-accent font-medium" : ""}
                          >
                            <ProjectPLIcon className="size-4" />
                            Project P&L
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("project-forecast", "sub-project-forecast")}
                            className={activeItemId === "sub-project-forecast" ? "bg-accent font-medium" : ""}
                          >
                            <TrendingUp className="size-4" />
                            Project Forecast
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("profitability", "sub-profitability")}
                            className={activeItemId === "sub-profitability" ? "bg-accent font-medium" : ""}
                          >
                            <ProfitabilityIcon className="size-4" />
                            Profitability Analysis
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("audit-log", "sub-audit-log")}
                            className={activeItemId === "sub-audit-log" ? "bg-accent font-medium" : ""}
                          >
                            <AuditLogIcon className="size-4" />
                            Audit Log
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  );
                }
                
                return (
                  <SidebarMenuItem>
                    <Collapsible
                      open={openMenus["reports-menu"]}
                      onOpenChange={() => toggleMenu("reports-menu")}
                      className="group/collapsible"
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Reports" hasSubmenu>
                          <ReportsIcon />
                          <span className="group-data-[collapsible=icon]:hidden">Reports</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Project Transactions */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("project-transactions", "sub-project-transactions")}
                              data-active={activeItemId === "sub-project-transactions"}
                              className={activeItemId === "sub-project-transactions" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <ProjectTransactionsIcon />
                              <span>Project Transactions</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* WIP (Work In Progress) */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("wip-reporting", "sub-wip-reporting")}
                                    data-active={activeItemId === "sub-wip-reporting"}
                                    className={activeItemId === "sub-wip-reporting" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <WIPIcon />
                                    <span>WIP</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Work In Progress</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Project Status Report */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("project-status", "sub-project-status")}
                                    data-active={activeItemId === "sub-project-status"}
                                    className={activeItemId === "sub-project-status" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <Activity className="size-4" />
                                    <span>Project Status</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Operational Reports</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Budget Variance */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("budget-variance", "sub-budget-variance")}
                                    data-active={activeItemId === "sub-budget-variance"}
                                    className={activeItemId === "sub-budget-variance" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <BudgetVarianceIcon className="size-4" />
                                    <span>Budget Variance</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Financial Reports</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Project P&L */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("project-pl", "sub-project-pl")}
                                    data-active={activeItemId === "sub-project-pl"}
                                    className={activeItemId === "sub-project-pl" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <ProjectPLIcon className="size-4" />
                                    <span>Project P&L</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Financial Reports</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Project Forecast */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("project-forecast", "sub-project-forecast")}
                                    data-active={activeItemId === "sub-project-forecast"}
                                    className={activeItemId === "sub-project-forecast" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <TrendingUp className="size-4" />
                                    <span>Project Forecast</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Financial Reports</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Profitability Analysis */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("profitability", "sub-profitability")}
                                    data-active={activeItemId === "sub-profitability"}
                                    className={activeItemId === "sub-profitability" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <ProfitabilityIcon className="size-4" />
                                    <span>Profitability Analysis</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Financial Reports</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Audit Log */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("audit-log", "sub-audit-log")}
                              data-active={activeItemId === "sub-audit-log"}
                              className={activeItemId === "sub-audit-log" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <AuditLogIcon />
                              <span>Audit Log</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })()}

              {/* Gantt Charts — hidden for Release 1 */}
              {!menuItems.find((item) => item.id === "gantt")?.hidden && (
                <SidebarMenuItem>
                  {renderMenuItem(menuItems.find((item) => item.id === "gantt")!, "main-gantt")}
                </SidebarMenuItem>
              )}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter>
        {/* Settings Menu (Collapsible with sub-items / Flyout when collapsed) - visible when user has any settings permission */}
        {menuConfig.showSettings && hasAnySettingsAccess && (
          <SidebarMenu>
            <SidebarMenuItem>
              {isCollapsed ? (
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton hasSubmenu>
                          <Settings />
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="flex items-center gap-1">
                        Settings
                        <ChevronRight className="size-3 opacity-60" />
                      </span>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent side="right" align="end" className="min-w-[180px]">
                    <DropdownMenuLabel>Settings</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {canShowSettingsItem('settings-general') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-general", "sub-settings-general")}
                        className={activeItemId === "sub-settings-general" ? "bg-accent font-medium" : ""}
                      >
                        <Settings className="size-4" />
                        General Settings
                      </DropdownMenuItem>
                    )}
                    {canShowSettingsItem('settings-project') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-project", "sub-settings-project")}
                        className={activeItemId === "sub-settings-project" ? "bg-accent font-medium" : ""}
                      >
                        <Settings className="size-4" />
                        Project Settings
                      </DropdownMenuItem>
                    )}
                    {canShowSettingsItem('settings-integrations') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-integrations", "sub-settings-integrations")}
                        className={activeItemId === "sub-settings-integrations" ? "bg-accent font-medium" : ""}
                      >
                        <Settings className="size-4" />
                        Integrations
                      </DropdownMenuItem>
                    )}
                    {canShowSettingsItem('settings-user-permissions') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-user-permissions", "sub-settings-user-permissions")}
                        className={activeItemId === "sub-settings-user-permissions" ? "bg-accent font-medium" : ""}
                      >
                        <Shield className="size-4" />
                        Roles &amp; Permissions
                      </DropdownMenuItem>
                    )}
                    {canShowSettingsItem('settings-templates') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-templates", "sub-settings-templates")}
                        className={activeItemId === "sub-settings-templates" ? "bg-accent font-medium" : ""}
                      >
                        <FileText className="size-4" />
                        Templates
                      </DropdownMenuItem>
                    )}
                    {canShowSettingsItem('settings-notifications') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-notifications", "sub-settings-notifications")}
                        className={activeItemId === "sub-settings-notifications" ? "bg-accent font-medium" : ""}
                      >
                        <Bell className="size-4" />
                        Notifications
                      </DropdownMenuItem>
                    )}
                    {/* Feature Labs hidden for phase 1 release */}
                    {/* {canShowSettingsItem('settings-feature-labs') && (
                      <DropdownMenuItem
                        onClick={() => handleItemClick("settings-feature-labs", "sub-settings-feature-labs")}
                        className={activeItemId === "sub-settings-feature-labs" ? "bg-accent font-medium" : ""}
                      >
                        <FlaskConical className="size-4" />
                        Feature Labs
                      </DropdownMenuItem>
                    )} */}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Collapsible
                  open={openMenus["settings"]}
                  onOpenChange={(isOpen) => {
                    toggleMenu("settings");
                    if (isOpen) {
                      // Navigate to the first permitted settings sub-item
                      const firstPermitted = Object.keys(settingsPermissionMap).find(canShowSettingsItem);
                      if (firstPermitted) {
                        handleItemClick(firstPermitted, `sub-${firstPermitted}`);
                      }
                    }
                  }}
                  className="group/collapsible"
                >
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Settings" hasSubmenu>
                      <Settings />
                      <span>Settings</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {/* General Settings */}
                      {canShowSettingsItem('settings-general') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-general", "sub-settings-general")}
                            data-active={activeItemId === "sub-settings-general"}
                            className={activeItemId === "sub-settings-general" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <Settings />
                            <span>General Settings</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {/* Project Settings */}
                      {canShowSettingsItem('settings-project') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-project", "sub-settings-project")}
                            data-active={activeItemId === "sub-settings-project"}
                            className={activeItemId === "sub-settings-project" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <Settings />
                            <span>Project Settings</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {/* Integrations */}
                      {canShowSettingsItem('settings-integrations') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-integrations", "sub-settings-integrations")}
                            data-active={activeItemId === "sub-settings-integrations"}
                            className={activeItemId === "sub-settings-integrations" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <Settings />
                            <span>Integrations</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}

                      {/* Roles & Permissions */}
                      {canShowSettingsItem('settings-user-permissions') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-user-permissions", "sub-settings-user-permissions")}
                            data-active={activeItemId === "sub-settings-user-permissions"}
                            className={activeItemId === "sub-settings-user-permissions" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <Shield />
                            <span>Roles &amp; Permissions</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}

                      {/* Templates */}
                      {canShowSettingsItem('settings-templates') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-templates", "sub-settings-templates")}
                            data-active={activeItemId === "sub-settings-templates"}
                            className={activeItemId === "sub-settings-templates" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <FileText />
                            <span>Templates</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}

                      {/* Notifications */}
                      {canShowSettingsItem('settings-notifications') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-notifications", "sub-settings-notifications")}
                            data-active={activeItemId === "sub-settings-notifications"}
                            className={activeItemId === "sub-settings-notifications" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <Bell />
                            <span>Notifications</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}

                      {/* Feature Labs hidden for phase 1 release */}
                      {/* {canShowSettingsItem('settings-feature-labs') && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleItemClick("settings-feature-labs", "sub-settings-feature-labs")}
                            data-active={activeItemId === "sub-settings-feature-labs"}
                            className={activeItemId === "sub-settings-feature-labs" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                          >
                            <FlaskConical />
                            <span>Feature Labs</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )} */}

                    </SidebarMenuSub>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {/* Database Switcher */}
        {menuConfig.showDatabaseSwitcher && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <DatabaseSwitcher
              databases={databases}
              selectedDatabase={selectedDatabase}
              onDatabaseChange={setSelectedDatabase}
              label="Database"
            />
          </>
        )}
        {/* Resume Onboarding — visible to admins only while onboarding is incomplete */}
        {isAdmin && !hasCompleted && (
          <>
            <Separator />
            <div className="pt-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={openWizard}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors group-data-[state=collapsed]:justify-center"
                    >
                      <PlayCircle className="h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="group-data-[state=collapsed]:hidden truncate">
                        Resume Onboarding
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="group-data-[state=expanded]:hidden">
                    Resume Onboarding
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </>
        )}
        <Separator />
        <div className="pt-2">
          <CompanySelector variant="sidebar" collapsed={state === 'collapsed'} />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
